import { useTranslation } from '@/i18n/useTranslation';

import { Button } from './Button';
import { useDeadEndHelp } from './deadEndHelp';

export interface DeadEndHelpButtonProps {
  /** Stretch to the footer's width, as a lone stacked action does. */
  readonly fullWidth?: boolean;
}

/**
 * ⭐ **A sign-in dead end's OWN help control: one tap raises the entry** [03-us-kid-7 § Actors and the first
 * scenario, with its 2026-10-06 note; 0230 § Decision 1]. Rendered by the lockout (`ThrottledScreen`) and the
 * neutral hand-off (`SeeARepScreen`).
 *
 * ⛔ **It must sit inside a `<KioskScreen signInDeadEnd>`.** It names the screen's own *Need help?* control
 * (`useDeadEndHelp`), so on those screens it raises `needHelpAtSignIn` — *stuck at sign-in* [0461; 0168] —
 * and the pill and this button can never disagree. Outside a `KioskScreen` it throws.
 *
 * - No name is asked: the candidate is stuck precisely because the kiosk cannot identify them.
 * - It skips the *Need help?* overlay's launch and name states and opens it straight on `in the recruiter
 *   queue` — the confirmation that someone is coming, with no time promised [0457 (c)].
 * - A repeat tap sends nothing: `summonRepresentative` is once per candidate session [KOP-1 AC-14], so the
 *   overlay reopens on the same queue state. Unless the earlier write did not land: the store then gives the
 *   control back, and this tap raises again [0500 point 3; 0220 L1 — a write that did not land gives the
 *   human control back].
 * - ↻ **On the neutral hand-off the first tap sends nothing either** [0500 point 5]: reaching `SeeARepScreen`
 *   already raised the entry (`KioskScreen`'s `raisesOnArrival`), so there the tap only opens the queue state.
 *   On the lockout the tap still raises.
 * - It touches nothing of the throttle — asking for help is not a way to reset it (*The lockout timer is
 *   unaffected*).
 *
 * ⚠️ The check-in form's inline lookup error is a sign-in dead end too (the Operator, 2026-10-14), but it has
 * no button of its own: only the *Need help?* press there was ruled. Whether it gets one is KID-7's list to
 * write [0230 § Decision 1], not this component's.
 *
 * ⚠️ **No frame draws it** — `04 · See a rep` draws only the pill, and the lockout has no frame at all. So it
 * is the one-tap control the app already ships, `SubmissionFailedScreen`'s secondary *Get help*, with the same
 * words; recorded in `design-qa/known-departures.md`.
 */
export function DeadEndHelpButton({ fullWidth = false }: DeadEndHelpButtonProps) {
  const t = useTranslation();
  const { askForHelp } = useDeadEndHelp();

  return (
    <Button fullWidth={fullWidth} onClick={askForHelp} variant="secondary">
      {t('deadEnd.getHelp')}
    </Button>
  );
}
